import { useState } from "react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Button } from "@/components/ui/Button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card"
import ChartLegend from "./components/ChartLegend"
import AddProductsSheet from "./components/AddProductsSheet"
import ListProductCard from "./components/ListProductCard"
import { type ListProduct, type ListStatus, CANDIDATES, ON_LIST, units } from "./listData"

// The list the buyer works on. Arriving products are a separate card below:
// nobody picked them, they are only there to be seen.
const GROUPS: { status: ListStatus; label: string }[] = [
  { status: "draft", label: "Not started" },
  { status: "in_progress", label: "In progress" },
]

function GroupLabel({ children }: { children: string }) {
  return <div className="border-b bg-muted/60 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide">{children}</div>
}

// The purchase list with the add area in a side sheet instead of below the
// list. Drafts come first, newest on top, so a product just added lands
// right under the header while the sheet is still open.
export default function PurchaseList() {
  const [onList, setOnList] = useState<ListProduct[]>(ON_LIST)
  const [candidates, setCandidates] = useState<ListProduct[]>(CANDIDATES)
  const [adding, setAdding] = useState(false)

  const add = (product: ListProduct) => {
    setCandidates(candidates.filter((p) => p.id !== product.id))
    setOnList([{ ...product, status: "draft", justAdded: true }, ...onList])
  }
  const remove = (id: string) => {
    const product = onList.find((p) => p.id === id)
    setOnList(onList.filter((p) => p.id !== id))
    if (product) setCandidates([{ ...product, justAdded: false }, ...candidates])
  }
  const open = () => {
    setOnList(onList.map((p) => ({ ...p, justAdded: false })))
    setAdding(true)
  }

  const bought = onList.filter((p) => p.status !== "arriving")
  const total = bought.flatMap((p) => p.lines).reduce((sum, line) => sum + line.quantity, 0)
  const drafts = onList.filter((p) => p.status === "draft")
  const arriving = onList.filter((p) => p.status === "arriving")

  return (
    <AppLayout header={<PageTitle title="Purchase Plan" subtitle={`${bought.length} products to buy · ${units(total)} units. All markets.`} />}>
      <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-6 gap-y-2 space-y-0">
          <CardTitle>Purchase list</CardTitle>
          <div className="flex flex-wrap items-center gap-4">
            <ChartLegend />
            <Button size="sm" onClick={open}>Add products</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border bg-card">
            {GROUPS.map(({ status, label }) => {
              const products = onList.filter((p) => p.status === status)
              return (
                <div key={status}>
                  <GroupLabel>{`${label} · ${products.length}`}</GroupLabel>
                  {status === "draft" && drafts.length === 0 && (
                    <div className="flex items-center justify-center gap-3 border-b px-4 py-6 text-sm">
                      Nothing picked to buy yet.
                      <Button variant="outline" size="sm" onClick={open}>Add products</Button>
                    </div>
                  )}
                  {products.map((product) => <ListProductCard key={product.id} product={product} onRemove={remove} />)}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Arriving, not on the list</CardTitle>
          <CardDescription>Orders on the way for products nobody picked to buy. Shown so the buyer knows what is coming; nothing to do here.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border bg-card">
            {arriving.map((product) => <ListProductCard key={product.id} product={product} />)}
          </div>
        </CardContent>
      </Card>
      </div>
      <AddProductsSheet open={adding} onOpenChange={setAdding} candidates={candidates} onAdd={add} />
    </AppLayout>
  )
}
